import { useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Download,
  FileSearch,
  Loader2,
  Lock,
  Search,
  ShieldOff,
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { extractErrorMessage } from '@/lib/api-client';
import { formatDate } from '@/lib/format';
import { useDriversList } from '@/features/drivers/api';
import {
  TIPO_PEDIDO_LABEL,
  useAnonimizar,
  useConcluirPedido,
  useExportarDados,
  usePedidosLgpd,
  useSumarioTitular,
  type SumarioTitular,
} from './api';

/**
 * Direitos do titular (LGPD).
 *
 * O titular aqui é o motorista — que não é cliente da plataforma e muitas
 * vezes nem sabe que ela existe. Ele exerce o direito pelo empregador, e é o
 * empregador que opera esta tela.
 *
 * Duas decisões de interface que vêm disso:
 *
 * - **A anonimização exige digitar o nome do titular.** É irreversível e
 *   derruba o acesso de uma pessoa; um clique em "confirmar" é barato demais
 *   para uma ação que não tem volta.
 * - **O prazo aparece em dias, não como data.** A lei dá 15 dias e a multa da
 *   ANPD é pelo atraso; "faltam 2 dias" comunica urgência de um jeito que
 *   "vence em 22/08" não comunica.
 */
export function LgpdPage() {
  const [busca, setBusca] = useState('');
  const [selecionado, setSelecionado] = useState<{ id: string; nome: string } | null>(null);

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Dados pessoais e LGPD</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Atenda pedidos de motoristas sobre os dados que a empresa mantém. O prazo legal
          de resposta é de 15 dias.
        </p>
      </div>

      <BuscaTitular
        busca={busca}
        onBusca={setBusca}
        selecionado={selecionado}
        onSelecionar={setSelecionado}
      />

      {selecionado && <PainelTitular titular={selecionado} />}

      <Pedidos />
    </div>
  );
}

// -----------------------------------------------------------------------------

function BuscaTitular({
  busca,
  onBusca,
  selecionado,
  onSelecionar,
}: {
  busca: string;
  onBusca: (v: string) => void;
  selecionado: { id: string; nome: string } | null;
  onSelecionar: (v: { id: string; nome: string } | null) => void;
}) {
  const lista = useDriversList({ q: busca || undefined, limit: 8 });

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="text-base">Encontrar o titular</CardTitle>
        <CardDescription>Busque pelo nome ou CPF do motorista.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Nome ou CPF"
            value={busca}
            onChange={(e) => onBusca(e.target.value)}
          />
        </div>

        {busca.length > 0 && (
          <div className="divide-y divide-border overflow-hidden rounded-lg border border-border">
            {lista.isLoading && <Skeleton className="h-16 w-full" />}
            {!lista.isLoading && (lista.data?.data.length ?? 0) === 0 && (
              <p className="p-4 text-sm text-muted-foreground">
                Nenhum motorista encontrado.
              </p>
            )}
            {lista.data?.data.map((motorista) => (
              <button
                key={motorista.id}
                type="button"
                onClick={() => onSelecionar({ id: motorista.id, nome: motorista.name })}
                className={`flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-accent ${
                  selecionado?.id === motorista.id ? 'bg-accent' : ''
                }`}
              >
                <div>
                  <p className="text-sm font-medium">{motorista.name}</p>
                  <p className="font-mono text-xs text-muted-foreground">{motorista.cpf}</p>
                </div>
                {selecionado?.id === motorista.id && (
                  <Badge variant="info">selecionado</Badge>
                )}
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// -----------------------------------------------------------------------------

function PainelTitular({ titular }: { titular: { id: string; nome: string } }) {
  const sumario = useSumarioTitular(titular.id);
  const exportar = useExportarDados();
  const [anonimizarAberto, setAnonimizarAberto] = useState(false);

  if (sumario.isLoading) return <Skeleton className="h-72 w-full" />;
  if (!sumario.data) return null;

  const { titular: dadosTitular, dados } = sumario.data;

  return (
    <Card>
      <CardHeader className="flex-row flex-wrap items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2 text-base">
            {dadosTitular.nome}
            {dadosTitular.anonimizado && <Badge variant="secondary">anonimizado</Badge>}
          </CardTitle>
          <CardDescription>
            O que a empresa mantém, sob qual justificativa legal.
          </CardDescription>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={exportar.isPending}
            onClick={() =>
              exportar.mutate(titular.id, {
                onSuccess: () => toast.success('Arquivo baixado'),
                onError: (err) => toast.error(extractErrorMessage(err)),
              })
            }
          >
            {exportar.isPending ? (
              <Loader2 className="mr-2 size-4 animate-spin" />
            ) : (
              <Download className="mr-2 size-4" />
            )}
            Exportar dados
          </Button>

          {!dadosTitular.anonimizado && (
            <Button
              variant="outline"
              size="sm"
              className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={() => setAnonimizarAberto(true)}
            >
              <ShieldOff className="mr-2 size-4" />
              Anonimizar
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent>
        <TabelaDeDados dados={dados} />
      </CardContent>

      <DialogAnonimizar
        aberto={anonimizarAberto}
        onAberto={setAnonimizarAberto}
        titular={dadosTitular}
        sumario={sumario.data}
      />
    </Card>
  );
}

function TabelaDeDados({ dados }: { dados: SumarioTitular['dados'] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-xs uppercase text-muted-foreground">
            <th className="pb-2 pr-4 font-medium">Categoria</th>
            <th className="pb-2 pr-4 text-right font-medium">Registros</th>
            <th className="pb-2 pr-4 font-medium">Base legal</th>
            <th className="pb-2 font-medium">Pode ser eliminado?</th>
          </tr>
        </thead>
        <tbody>
          {dados.map((d) => (
            <tr key={d.categoria} className="border-b border-border/60 last:border-0">
              <td className="py-3 pr-4">
                <p className="font-medium">{d.categoria}</p>
                <p className="text-xs text-muted-foreground">{d.descricao}</p>
              </td>
              <td className="py-3 pr-4 text-right font-mono tabular-nums">{d.registros}</td>
              <td className="py-3 pr-4 text-muted-foreground">{d.baseLegal}</td>
              <td className="py-3">
                {d.podeSerEliminado ? (
                  <Badge variant="success">sim</Badge>
                ) : (
                  <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Lock className="size-3" />
                    Retenção: {d.retencao}
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// -----------------------------------------------------------------------------

function DialogAnonimizar({
  aberto,
  onAberto,
  titular,
  sumario,
}: {
  aberto: boolean;
  onAberto: (v: boolean) => void;
  titular: SumarioTitular['titular'];
  sumario: SumarioTitular;
}) {
  const anonimizar = useAnonimizar();
  const [confirmacao, setConfirmacao] = useState('');
  const [motivo, setMotivo] = useState('');

  const preservados = sumario.dados.filter((d) => !d.podeSerEliminado && d.registros > 0);
  const podeConfirmar = confirmacao.trim() === titular.nome;

  return (
    <Dialog open={aberto} onOpenChange={onAberto}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="size-5 text-destructive" />
            Anonimizar {titular.nome}
          </DialogTitle>
          <DialogDescription>
            Esta ação não tem volta. Nome, CPF, RG, CNH, contato, endereço e os arquivos
            de documento serão apagados, e a pessoa perde o acesso ao aplicativo.
          </DialogDescription>
        </DialogHeader>

        {preservados.length > 0 && (
          <div className="rounded-lg border border-border bg-muted/40 p-4">
            <p className="flex items-center gap-2 text-sm font-medium">
              <Lock className="size-4" />O que continua guardado
            </p>
            <ul className="mt-2 space-y-1">
              {preservados.map((d) => (
                <li key={d.categoria} className="text-xs text-muted-foreground">
                  {d.registros} {d.categoria.toLowerCase()} — {d.retencao}
                </li>
              ))}
            </ul>
            <p className="mt-3 text-xs text-muted-foreground">
              A lei obriga a guardar esses registros, e o Art. 16 da LGPD permite. Eles
              deixam de identificar o titular.
            </p>
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label htmlFor="motivo-anon" className="text-sm font-medium">
              De onde veio o pedido
            </label>
            <Input
              id="motivo-anon"
              className="mt-1.5"
              placeholder="Ex.: e-mail do titular em 10/08/2026"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Fica no comprovante. É o que prova que houve pedido.
            </p>
          </div>

          <div>
            <label htmlFor="confirma-anon" className="text-sm font-medium">
              Digite <span className="font-mono">{titular.nome}</span> para confirmar
            </label>
            <Input
              id="confirma-anon"
              className="mt-1.5"
              value={confirmacao}
              onChange={(e) => setConfirmacao(e.target.value)}
              autoComplete="off"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onAberto(false)}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            disabled={!podeConfirmar || anonimizar.isPending}
            onClick={() =>
              anonimizar.mutate(
                { driverId: titular.id, motivo: motivo || undefined },
                {
                  onSuccess: (r) => {
                    toast.success(`Titular anonimizado. Comprovante: ${r.comprovante}`);
                    onAberto(false);
                    setConfirmacao('');
                    setMotivo('');
                  },
                  onError: (err) => toast.error(extractErrorMessage(err)),
                },
              )
            }
          >
            {anonimizar.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
            Anonimizar definitivamente
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------

function Pedidos() {
  const pedidos = usePedidosLgpd();
  const concluir = useConcluirPedido();

  const abertos = pedidos.data?.filter((p) => p.status === 'open') ?? [];
  const historico = pedidos.data?.filter((p) => p.status !== 'open').slice(0, 10) ?? [];

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="text-base">Solicitações</CardTitle>
        <CardDescription>
          Toda consulta e anonimização fica registrada aqui, com o prazo de resposta.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {pedidos.isLoading && <Skeleton className="h-24 w-full" />}

        {!pedidos.isLoading && abertos.length === 0 && historico.length === 0 && (
          <div className="flex flex-col items-center gap-2 py-8 text-center text-sm text-muted-foreground">
            <FileSearch className="size-8 opacity-40" />
            <p>Nenhuma solicitação registrada ainda.</p>
          </div>
        )}

        {abertos.map((p) => {
          const atrasado = (p.diasRestantes ?? 0) < 0;
          return (
            <div
              key={p.id}
              className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border p-4 ${
                atrasado ? 'border-destructive/50 bg-destructive/5' : 'border-border'
              }`}
            >
              <div>
                <p className="text-sm font-medium">
                  {TIPO_PEDIDO_LABEL[p.type] ?? p.type} · {p.subjectLabel}
                </p>
                <p
                  className={`mt-0.5 flex items-center gap-1.5 text-xs ${
                    atrasado ? 'font-medium text-destructive' : 'text-muted-foreground'
                  }`}
                >
                  <Clock className="size-3" />
                  {atrasado
                    ? `Atrasado há ${Math.abs(p.diasRestantes ?? 0)} dia(s)`
                    : `Faltam ${p.diasRestantes} dia(s) para responder`}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                disabled={concluir.isPending}
                onClick={() =>
                  concluir.mutate(
                    { id: p.id },
                    {
                      onSuccess: () => toast.success('Solicitação concluída'),
                      onError: (err) => toast.error(extractErrorMessage(err)),
                    },
                  )
                }
              >
                <CheckCircle2 className="mr-2 size-4" />
                Marcar como respondida
              </Button>
            </div>
          );
        })}

        {historico.length > 0 && (
          <div className="overflow-x-auto pt-2">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase text-muted-foreground">
                  <th className="pb-2 pr-4 font-medium">Tipo</th>
                  <th className="pb-2 pr-4 font-medium">Titular</th>
                  <th className="pb-2 font-medium">Concluída em</th>
                </tr>
              </thead>
              <tbody>
                {historico.map((p) => (
                  <tr key={p.id} className="border-b border-border/60 last:border-0">
                    <td className="py-2.5 pr-4">{TIPO_PEDIDO_LABEL[p.type] ?? p.type}</td>
                    <td className="py-2.5 pr-4 text-muted-foreground">{p.subjectLabel}</td>
                    <td className="py-2.5 text-muted-foreground">
                      {formatDate(p.completedAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
